import { DatePicker, Table } from "antd";
import Loader from "Components/Loader";
import { useGetCreditManagementQuery } from "hooks/CreditManagement/useGetCreditManagementQuery";
import DashboardLayout from "Layout/DashboardLayout";
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SearchBox } from "./SearchBox";
import { useDebounce } from "hooks/useDebounce";
import { cn } from "utils";
import KoboConverter from "Components/KoboConverter";

const CreditManagement = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [payload, setPayload] = useState({ startDate: "", endDate: "" });
  const [selectedDates, setSelectedDates] = useState<any>(null);

  const DEFAULT_TYPE = "";
  const [params] = useSearchParams();
  const ordersType = params.get("type") || DEFAULT_TYPE;

  const orderTypes = [
    {
      label: "All Credits",
      value: DEFAULT_TYPE,
    },
    {
      label: "Pending",
      value: "pending",
    },
    {
      label: "Approved",
      value: "approved",
    },
    {
      label: "Declined",
      value: "declined",
    },
    {
      label: "In Review",
      value: "in_review",
    },
    {
      label: "Repaid",
      value: "repaid",
    },
  ];

  const { data, isPending } = useGetCreditManagementQuery({
    search: debouncedSearch,
    startDate: payload?.startDate,
    endDate: payload?.endDate,
    status: ordersType,
  });

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user: any) => (
        <span>{user?.firstName + " " + user?.lastName}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <span>
          <KoboConverter amount={amount} />
        </span>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (reason: string) => <span>{reason}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={`capitalize ${status === "pending" && "text-yellow-500"}
            ${status === "in_review" && "text-blue-500"}
            ${status === "approved" && "text-green-500"}
            ${status === "repaid" && "text-yellow-500"}
            ${status === "declined" && "text-red-500"}`}
        >
          {status === "in_review" ? "In Review" : status}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "isPaid",
      key: "isPaid",
      render: (isPaid: boolean) => (
        <span className={isPaid ? "text-green-500" : "text-red-500"}>
          {JSON.stringify(isPaid)}
        </span>
      ),
    },
    {
      title: "ID",
      dataIndex: "applicationId",
      key: "applicationId",
    },
  ];

  const navigation = useNavigate();
  const { RangePicker } = DatePicker;

  return (
    <DashboardLayout
      title="Credit Management"
      back={false}
      right={
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search Order ID, Customer Name"
        />
      }
    >
      <div className="">
        {isPending && <Loader />}
        {!isPending && data?.data?.docs && (
          <>
            <div className="flex border-b border-[#F0F0F0]">
              {orderTypes.map((item) => (
                <Link
                  key={item.value}
                  to={`/credit-management?type=${item.value}`}
                  className={cn(
                    "px-5 py-6 text-primary-foreground cursor-pointer hover:bg-orange/10",
                    {
                      "bg-primary-light text-orange": item.value === ordersType,
                    }
                  )}
                >
                  <div className="">{item.label}</div>
                </Link>
              ))}
            </div>
            <div className="p-[10px]">
              <div className="flex justify-end py-[20px] mr-[40px]">
                <RangePicker
                  value={selectedDates}
                  showTime
                  onCalendarChange={(dates, dateStrings) => {
                    if (dates && dateStrings[0] && dateStrings[1]) {
                      const startDate = new Date(dateStrings[0]).toISOString();
                      const endDate = new Date(dateStrings[1]).toISOString();
                      setPayload({ startDate: startDate, endDate: endDate });
                      setSelectedDates(dates);
                    } else {
                      setSelectedDates(null);
                      setPayload({ startDate: "", endDate: "" });
                    }
                  }}
                />
              </div>
              <Table
                rowKey="id"
                columns={columns}
                onRow={(record: any) => ({
                  onClick: () => navigation(`/credit-management/${record?.id}`),
                })}
                scroll={{ y: "50vh" }}
                dataSource={data?.data?.docs}
                className="manrope"
              />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreditManagement;
